import * as React from 'react';
import { HistoryOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import { isEmpty } from 'lodash';
import { SUBMIT_BUTTON_LABEL } from '@constants/common';
import { StatusCodes } from '@constants/enum/common';
import { SEARCH_PLACEHOLDER } from '@constants/prompt-explorer';
import { IPipeline, IPromptPlayground, ISearchResult, PlaygroundId } from '@redux/types/types';
import SearchHistoryLog from '@components/search/organisms/searchHistoryLog/SearchHistoryLog';
import { UpdatePrompt } from '@pages/promptExplorer/PromptExplorer';
import styles from './RenderPromptExplorer.module.scss';
import PipelinePlayground from '../PipelinePlayground';

const { Search } = Input;

interface IRenderPromptExplorerProps {
  historyVisible: boolean;
  playgrounds: IPromptPlayground[];
  getPipelineByName: (pipelineName: string) => IPipeline | null;
  queryStatus: Record<string, StatusCodes>;
  displayTypingEffect: boolean;
  queryValue: string;
  sessionQueriesResults: Record<PlaygroundId, ISearchResult[]>;
  promptEditorHeight: number;
  onPromptEditorValueChange: ({ playgroundId, update, value }: UpdatePrompt) => void;
  getPlaygroundById: (playgroundId: string) => IPromptPlayground | null;
  queryResults: Record<string, ISearchResult[]>;
  onPromptEditorPromptNodeChange: ({
    node,
    playgroundId,
  }: {
    node: string;
    playgroundId: string;
  }) => void;
  handleRemovingPlayground: (playgroundId: string) => void;
  handleClearingPlayground: (playgroundId: string) => void;
  handleSelectingPipeline: ({
    pipelineName,
    playgroundId,
  }: {
    pipelineName: string;
    playgroundId: string;
  }) => Promise<void>;
  handleAddingNewPlayground: (playgroundId: string, pipelineName: string) => Promise<void>;
  setActivePlaygroundFiltersDrawer: React.Dispatch<React.SetStateAction<string>>;
  handleStartingNewPlaygroundSession: (playgroundId: string) => void;
  promptEditorContainerExpanded: boolean;
  handleRepeatingQuery: (query: string) => void;
  handleSavingQueryPromptAsTemplate: (playgroundId: string, prompt: string) => void;
  initialPrompts: string[];
  handlePromptEditorResize: (height: number) => void;
  onUpdatePipelinePrompt: (playgroundId: string) => void;
  setPromptEditorContainerExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setActivePlaygroundPromptTemplate: (playgroundId: string) => void;
  closeSearchHistory: () => void;
  openSearchHistory: () => void;
  isSearching: () => boolean;
  onInputQueryChange: (newQuery: string) => void;
  onQuery: () => void;
  onSessionSelect: (sessionId: string) => void;
  onActivatePipeline: (playgroundId: string) => void;
}

type IRenderHistoryProps = {
  playgrounds: IPromptPlayground[];
  getPipelineByName: (pipelineName: string) => IPipeline | null;
  onSessionSelect: (sessionId: string) => void;
  closeSearchHistory: () => void;
};
const RenderHistory: React.FC<IRenderHistoryProps> = ({
  playgrounds,
  getPipelineByName,
  onSessionSelect,
  closeSearchHistory,
}) => {
  const [firstPlayground] = playgrounds;
  const { pipeline_id: pipelineId } = getPipelineByName(firstPlayground.pipelineName!) || {
    pipeline_id: '',
  };

  return (
    <div className={styles.history}>
      <SearchHistoryLog
        currentSessionId={firstPlayground.session!}
        pipelineId={pipelineId}
        pipelineName={firstPlayground.pipelineName!}
        onSessionChange={onSessionSelect}
        onClose={closeSearchHistory}
      />
    </div>
  );
};

export const RenderPromptExplorer: React.FC<IRenderPromptExplorerProps> = ({
  historyVisible,
  playgrounds,
  getPipelineByName,
  queryStatus,
  displayTypingEffect,
  queryValue,
  sessionQueriesResults,
  promptEditorHeight,
  onPromptEditorValueChange,
  getPlaygroundById,
  queryResults,
  onPromptEditorPromptNodeChange,
  handleRemovingPlayground,
  handleClearingPlayground,
  handleSelectingPipeline,
  handleAddingNewPlayground,
  setActivePlaygroundFiltersDrawer,
  handleStartingNewPlaygroundSession,
  promptEditorContainerExpanded,
  setActivePlaygroundPromptTemplate,
  handleRepeatingQuery,
  handleSavingQueryPromptAsTemplate,
  initialPrompts,
  handlePromptEditorResize,
  onUpdatePipelinePrompt,
  setPromptEditorContainerExpanded,
  closeSearchHistory,
  openSearchHistory,
  isSearching,
  onInputQueryChange,
  onQuery,
  onSessionSelect,
  onActivatePipeline,
}) => {
  return (
    <div className={styles.promptExplorer}>
      <div className={`${styles.promptExplorer_wrapper} ${historyVisible && styles.shrinked}`}>
        <div
          className={styles.promptExplorer_dynamicContent}
          style={{
            gridTemplateColumns: `repeat(${playgrounds.length}, ${100 / playgrounds.length}%)`,
          }}
        >
          {playgrounds.map((playground, playgroundIdx) => (
            <PipelinePlayground
              displayPipelineActionsBar
              key={playground.id}
              playground={playground}
              totalPlaygrounds={playgrounds.length}
              pipeline={getPipelineByName(playground.pipelineName || '')}
              search={{
                query: queryValue,
                results: sessionQueriesResults[playground.id] || queryResults[playground.id],
                status: queryStatus[playground.id] ?? StatusCodes.IDLE,
                searching: queryStatus[playground.id] === StatusCodes.IN_PROGRESS,
                displayTypingEffect,
              }}
              options={{
                displayRemovePipelineButton: playgroundIdx !== 0,
                displayHeader: playgroundIdx === 0,
                displayExpandShrinkButton: playgroundIdx + 1 === playgrounds.length,
                renderDescription: playgroundIdx === 0,
              }}
              promptEditorHeight={promptEditorHeight}
              activeFilters={!isEmpty(getPlaygroundById(playground.id)?.filters)}
              onPromptEditorValueChange={onPromptEditorValueChange}
              onPromptEditorPromptNodeChange={onPromptEditorPromptNodeChange}
              onSelectPipeline={handleSelectingPipeline}
              onAddNewPipeline={handleAddingNewPlayground}
              onRemovePipeline={handleRemovingPlayground}
              onDisplayFilters={setActivePlaygroundFiltersDrawer}
              onStartNewSession={handleStartingNewPlaygroundSession}
              onClearChat={handleClearingPlayground}
              promptEditorContainerExpanded={promptEditorContainerExpanded}
              onExpandShrinkPromptEditorContainer={() => {
                setPromptEditorContainerExpanded(!promptEditorContainerExpanded);
              }}
              onTemplatesLinkClick={setActivePlaygroundPromptTemplate}
              onRepeatQueryOptionClick={handleRepeatingQuery}
              onSaveQueryPromptTemplateOptionClick={handleSavingQueryPromptAsTemplate}
              onPromptEditorResize={handlePromptEditorResize}
              onUpdatePipelinePrompt={onUpdatePipelinePrompt}
              initialPrompts={initialPrompts}
              onActivatePipeline={onActivatePipeline}
            />
          ))}
        </div>
        <div className={styles.promptExplorer_query}>
          {playgrounds.length === 1 && playgrounds[0].pipelineName && (
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => (historyVisible ? closeSearchHistory() : openSearchHistory())}
            />
          )}
          <Search
            className={styles.promptExplorer_query_input}
            placeholder={SEARCH_PLACEHOLDER}
            disabled={isSearching()}
            enterButton={
              <Button
                type="primary"
                icon={<SendOutlined />}
                disabled={!queryValue || isSearching() || !playgrounds[0]?.pipelineName}
                loading={isSearching()}
              >
                {queryValue ? SUBMIT_BUTTON_LABEL : ''}
              </Button>
            }
            value={queryValue}
            onChange={(event) => onInputQueryChange(event.target.value)}
            onSearch={() => queryValue && onQuery()}
            autoFocus
          />
        </div>
      </div>
      {historyVisible && (
        <RenderHistory
          playgrounds={playgrounds}
          getPipelineByName={getPipelineByName}
          onSessionSelect={onSessionSelect}
          closeSearchHistory={closeSearchHistory}
        />
      )}
    </div>
  );
};
